import globalMessage from "@/utils/globalMessage";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Spin, Modal, Row, Col, InputNumber, Table, Upload, Tooltip, Collapse } from "antd";
import ResponsiveModal from "@components/ResponsiveModal";
import { api } from "@redux/api";
import ResponsiveFileManagement from "@components/FileManagement/ResponsiveFileManagement";
import { useSelector } from "react-redux";
import moment from "moment";

import CustomDatePicker from "@components/CustomDatePicker";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import {
    DeleteOutlined,
    PlusOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import {
    buildFacilityPath,
    extractContextFromPath,
    getFacilityContext,
    getLeafFromPathname,
} from "@/utils/route-context";
import {
    FormTemplateOptionLabel,
    formTemplateOptionSearchText,
} from "@components/FormTemplateOptionLabel";
import { getFormTemplateSelectLabel } from "@/utils/form-template-label";
import { filterFormsByFormType } from "@/utils/filter-forms-by-form-type";
import { effectiveUiFormType } from "@/utils/form-type-generic-compat";
import withResizableColumns from "@components/Table/withResizableColumns";
import DetailTabs from "@components/DetailTabs";
import "./index.css";
import Select from "@components/ResponsiveSelect";
import MobileTestItemsCardList from "./MobileTestItemsCardList";

const ResizableTable = withResizableColumns(Table);

const { TabPane } = DetailTabs;
const { Panel } = Collapse;
const { TextArea } = Input;
const { Option } = Select;

const FORM_TYPE_UI_LABELS = {
    functional_test: "Functional Test",
    pm_checklist: "PM Checklist",
    generic: "Generic",
};
const formTypeChoiceLabel = (key) => {
    if (key == null || (typeof key === "string" && key.trim() === "")) return "—";
    const k =
        typeof key === "string" ? key.trim().toLowerCase() : String(key);
    if (FORM_TYPE_UI_LABELS[k] != null) return FORM_TYPE_UI_LABELS[k];
    return typeof key === "string" && key.trim() !== "" ? key : "—";
};

/** Select 须在 options 中包含当前 value；否则整块不渲染标签。并入一条 getFormById 返回的记录。 */
const mergeTemplateRecordIntoTemplateOptionsMerge = (
    incomingBase,
    templateRecord
) => {
    const base = Array.isArray(incomingBase) ? [...incomingBase] : [];
    if (
        templateRecord == null ||
        typeof templateRecord !== "object" ||
        templateRecord.id === undefined ||
        templateRecord.id === null
    ) {
        return base;
    }
    const sid = String(templateRecord.id);
    if (base.some((x) => x != null && String(x?.id) === sid)) return base;
    const blankLead = base.filter((x) => x && x.id === "");
    const tail = base.filter((x) => !(x && x.id === ""));
    return [...blankLead, ...tail, templateRecord];
};

/**
 * 模板 boolean 列（Pass/Fail）：默认 null；点 Pass/Fail 选中，再次点击同一按钮则清除。
 * 不适用场景请使用模板列类型 N/A（`na`），勿在本控件内再提供 N/A。
 */
const PassFailToggleButtons = ({ value, onChange, readOnly }) => {
    const passOn = value === true;
    const failOn = value === false;

    if (readOnly) {
        if (passOn) {
            return (
                <span className="functional-testing-passfail-readonly functional-testing-passfail-readonly--pass">
                    <CheckCircleOutlined className="functional-testing-passfail-readonly__icon" />
                    Pass
                </span>
            );
        }
        if (failOn) {
            return (
                <span className="functional-testing-passfail-readonly functional-testing-passfail-readonly--fail">
                    <CloseCircleOutlined className="functional-testing-passfail-readonly__icon" />
                    Fail
                </span>
            );
        }
        return (
            <span className="functional-testing-passfail-readonly functional-testing-passfail-readonly--empty">
                Not set
            </span>
        );
    }

    return (
        <div className="functional-testing-passfail-wrap">
            <div className="functional-testing-passfail-seg" role="group" aria-label="Pass or Fail">
                <Button
                    type="default"
                    size="small"
                    htmlType="button"
                    aria-pressed={passOn}
                    title={passOn ? "Click again to clear" : "Mark as Pass"}
                    className={
                        "functional-testing-passfail-pill functional-testing-passfail-pill--pass" +
                        (passOn ? " is-selected" : "")
                    }
                    icon={<CheckCircleOutlined />}
                    onClick={() => onChange?.(passOn ? null : true)}
                >
                    Pass
                </Button>
                <span className="functional-testing-passfail-divider" aria-hidden />
                <Button
                    type="default"
                    size="small"
                    htmlType="button"
                    aria-pressed={failOn}
                    title={failOn ? "Click again to clear" : "Mark as Fail"}
                    className={
                        "functional-testing-passfail-pill functional-testing-passfail-pill--fail" +
                        (failOn ? " is-selected" : "")
                    }
                    icon={<CloseCircleOutlined />}
                    onClick={() => onChange?.(failOn ? null : false)}
                >
                    Fail
                </Button>
            </div>
        </div>
    );
};

/** 与模板 / API 对齐：type 统一小写，避免 switch 落到 default 全部变成普通 Input */
const normalizeMeasurementColumnType = (type) => {
    const t = type != null ? String(type).trim().toLowerCase() : "";
    return t || "text";
};

/** 与表单模板 FunctionalFormFieldsEditor 存库规则一致（px） */
const FT_MEASUREMENT_COLUMN_WIDTH_MIN = 80;
const FT_MEASUREMENT_COLUMN_WIDTH_MAX = 660;
/** 与表单模板 `TEMPLATE_COLUMN_WIDTH_DEFAULT_PX` 一致 */
const FT_MEASUREMENT_COLUMN_DEFAULT_WIDTH_PX = 180;
/** boolean 列为双段 Pass/Fail；不适用请用模板 N/A 列 */
const FT_BOOLEAN_MEASUREMENT_COLUMN_MIN_WIDTH_PX = 136;
/** textarea / Notes 类列默认更宽 */
const FT_TEXTAREA_MEASUREMENT_COLUMN_MIN_WIDTH_PX = 280;
/** photo 列：缩略图 + 上传按钮 */
const FT_PHOTO_MEASUREMENT_COLUMN_MIN_WIDTH_PX = 220;
/** Test items 表体最大高度（表头固定） */
const FT_ITEMS_TABLE_BODY_MAX_HEIGHT_PX = 380;
const DETAIL_FORM_LABEL_SPAN = 6;
const DETAIL_FORM_WRAPPER_SPAN = 18;

const normalizeMeasurementColumnWidthPx = (raw) => {
    if (raw == null || raw === "") return null;
    const n = Math.round(Number(raw));
    if (Number.isNaN(n)) return null;
    return Math.min(FT_MEASUREMENT_COLUMN_WIDTH_MAX, Math.max(FT_MEASUREMENT_COLUMN_WIDTH_MIN, n));
};

const measurementColumnWidthPx = (col) => normalizeMeasurementColumnWidthPx(col?.width);

/** 表格列 render.width：普通列用模板/默认宽；boolean 列保证不小于 Pass/Fail 控件最小宽度 */
const measurementColumnTableWidthPx = (col) => {
    const explicit = measurementColumnWidthPx(col);
    const base = explicit ?? FT_MEASUREMENT_COLUMN_DEFAULT_WIDTH_PX;
    const t = normalizeMeasurementColumnType(col?.type);
    const labelKey = `${col?.label ?? col?.id ?? ""}`.toLowerCase();
    if (t === "boolean") {
        const w = explicit ?? FT_BOOLEAN_MEASUREMENT_COLUMN_MIN_WIDTH_PX;
        return Math.max(w, FT_BOOLEAN_MEASUREMENT_COLUMN_MIN_WIDTH_PX);
    }
    if (t === "photo") {
        return Math.max(base, FT_PHOTO_MEASUREMENT_COLUMN_MIN_WIDTH_PX);
    }
    if (
        t === "textarea" ||
        labelKey.includes("note") ||
        labelKey.includes("comment")
    ) {
        return Math.max(base, FT_TEXTAREA_MEASUREMENT_COLUMN_MIN_WIDTH_PX);
    }
    return base;
};

/** KpiFiles.image → S3 key_name，供 generate_public_url 使用 */
function kpiFileImageToS3Key(imageField) {
    if (imageField == null || typeof imageField !== "string") return null;
    const s = imageField.trim();
    if (!s) return null;
    if (/^https?:\/\//i.test(s)) {
        try {
            const path = new URL(s).pathname.replace(/^\/+/, "");
            const marker = "facilitykpiclientfile/";
            const idx = path.indexOf(marker);
            if (idx >= 0) return path.slice(idx + marker.length);
            return path;
        } catch {
            return null;
        }
    }
    return s;
}

/** 上传与截图粘贴：部分浏览器粘贴为无 type 或 octet-stream */
function isAcceptableImageUpload(file) {
    if (!file) return false;
    const t = (file.type || "").toLowerCase().trim();
    const name = (file.name || "").toLowerCase();
    if (t.startsWith("image/")) return true;
    if (/\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/.test(name)) return true;
    if (t === "application/octet-stream" && file.size > 0 && file.size <= 40 * 1024 * 1024) return true;
    if (!t && file.size > 0 && file.size <= 40 * 1024 * 1024) return true;
    return false;
}

/**
 * 模板 type=photo：上传到文件服务（已保存的记录挂 functiontesting；新建则挂 equipment），JSON 存 KpiFiles 摘要。
 */
function FunctionalTestingPhotoFormControl({
    value,
    onChange,
    readOnly,
    equipmentId,
    facilityId,
    functionalTestingRecordId,
}) {
    const [previewUrl, setPreviewUrl] = React.useState(null);

    React.useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (!value || typeof value !== "object" || Array.isArray(value)) {
                setPreviewUrl(null);
                return;
            }
            const key = kpiFileImageToS3Key(value.image);
            if (!key) {
                setPreviewUrl(null);
                return;
            }
            try {
                const r = await api.generate_url(key, true);
                if (!cancelled) setPreviewUrl(r?.data?.url || null);
            } catch {
                if (!cancelled) setPreviewUrl(null);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [value]);

    const openSigned = async () => {
        const key = value?.image ? kpiFileImageToS3Key(value.image) : null;
        if (!key) return;
        try {
            const r = await api.generate_url(key, true);
            const u = r?.data?.url;
            if (u) window.open(u, "_blank", "noopener,noreferrer");
        } catch {
            globalMessage.error({ message: "Could not open file", description: "" });
        }
    };

    if (readOnly) {
        if (typeof value === "string" && value.trim()) {
            return <span>{value}</span>;
        }
        if (!value || typeof value !== "object" || (!value.image && value.kpi_file_id == null)) {
            return <span style={{ color: "#8c8c8c" }}>—</span>;
        }
        const name = value.name || "Photo";
        const mime = value.type != null ? String(value.type).toLowerCase() : "";
        const isRaster =
            mime.startsWith("image/") ||
            (typeof name === "string" && /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name));
        return (
            <div className="functional-testing-photo-cell functional-testing-photo-cell--readonly">
                {previewUrl && isRaster ? (
                    <button
                        type="button"
                        className="functional-testing-photo-thumb-wrap"
                        onClick={() => openSigned()}
                        title={typeof name === "string" ? `${name} — Preview` : "Preview"}
                    >
                        <img src={previewUrl} alt="" className="functional-testing-photo-thumb" />
                    </button>
                ) : null}
                <Button
                    type="link"
                    size="small"
                    onClick={() => openSigned()}
                    className="functional-testing-photo-open"
                    title={name}
                >
                    <span className="functional-testing-photo-filename">{name}</span>
                </Button>
            </div>
        );
    }

    const uploadFile = async (options) => {
        const { file, onError, onSuccess } = options;
        if (!facilityId || !equipmentId) {
            onError(new Error("missing equipment/facility"));
            return;
        }
        const tid =
            functionalTestingRecordId != null && String(functionalTestingRecordId).trim() !== ""
                ? String(functionalTestingRecordId)
                : null;
        const tableName = tid ? "functiontesting" : "equipment";
        const recordId = tid ? tid : String(equipmentId);
        const body = {
            table_name: tableName,
            record_id: recordId,
            facility: Number(facilityId),
            equipment: equipmentId,
            files: [{ originFileObj: file }],
            category: "Functional test",
            description: "",
            tags: [],
        };
        try {
            const res = await api.uploadFile(body);
            if (res.status >= 200 && res.status < 300 && Array.isArray(res.data) && res.data.length) {
                const f = res.data[0];
                onSuccess(f, file);
                onChange?.({
                    kpi_file_id: f.id,
                    image: f.image,
                    name: f.name,
                    type: f.type,
                });
            } else {
                const desc =
                    res?.message != null && typeof res.message === "object"
                        ? JSON.stringify(res.message)
                        : String(res?.message || res?.error || "Upload failed");
                globalMessage.error({ message: "Upload failed", description: desc });
                onError(res);
            }
        } catch (e) {
            globalMessage.error({ message: "Upload failed", description: String(e) });
            onError(e);
        }
    };

    const uploadReady = Boolean(facilityId && equipmentId);
    const uploadHint = !facilityId
        ? "无法识别当前设施：请从设施导航进入本页（地址需为 /facility/{公司ID}/{设施ID}/...）。"
        : !equipmentId
          ? "请先在上方选择「设备 (Equipment)」，再上传照片；保存本条功能测试前，文件会先挂到该设备下。"
          : null;

    const rasterThumb =
        previewUrl &&
        value &&
        typeof value === "object" &&
        (String(value.type || "").toLowerCase().startsWith("image/") ||
            /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(String(value.name || "")));

    const uploadButton = (
        <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={uploadFile}
            disabled={!uploadReady}
            beforeUpload={(file) => {
                if (!uploadReady) return Upload.LIST_IGNORE;
                if (!isAcceptableImageUpload(file)) {
                    globalMessage.warning({
                        message: "Please use an image file",
                        description: "File uploads support photos and screenshots (PNG, JPEG, etc.).",
                    });
                    return Upload.LIST_IGNORE;
                }
                return true;
            }}
        >
            <Button icon={<UploadOutlined />} size="small" type="default" disabled={!uploadReady}>
                {value?.kpi_file_id || value?.image ? "Replace image" : "Upload / paste image"}
            </Button>
        </Upload>
    );

    const fileLabel =
        value && typeof value === "object" && value.name != null && String(value.name).trim() !== ""
            ? String(value.name)
            : null;

    return (
        <div className="functional-testing-photo-cell">
            {rasterThumb ? (
                <>
                    <div className="functional-testing-photo-thumb-wrap functional-testing-photo-thumb-wrap--static">
                        <img
                            src={previewUrl}
                            alt=""
                            className="functional-testing-photo-thumb functional-testing-photo-thumb--edit"
                        />
                    </div>
                    {fileLabel ? (
                        <span className="functional-testing-photo-filename" title={fileLabel}>
                            {fileLabel}
                        </span>
                    ) : null}
                </>
            ) : null}
            {uploadReady ? (
                uploadButton
            ) : (
                <Tooltip title={uploadHint}>
                    <span className="functional-testing-photo-upload-wrap">{uploadButton}</span>
                </Tooltip>
            )}
            {uploadHint ? (
                <span className="functional-testing-photo-hint" role="note">
                    {uploadHint}
                </span>
            ) : null}
            {(value?.kpi_file_id || value?.image) && (
                <Button type="link" size="small" onClick={() => onChange?.(null)} style={{ paddingLeft: 0 }}>
                    Clear
                </Button>
            )}
        </div>
    );
}

/** 设施端测量列：补齐 id / label / type，供 Form.Item name 与控件分支使用 */
const normalizeMeasurementColumnsFromTemplate = (columns) => {
    if (!Array.isArray(columns)) return [];
    return columns
        .filter((c) => c != null && typeof c === "object")
        .map((c, index) => {
            const id =
                c.id != null && String(c.id) !== ""
                    ? c.id
                    : `col_${index}_${String(c.label || c.name || "field")
                        .replace(/\s+/g, "_")
                        .replace(/[^a-zA-Z0-9_]/g, "")
                        .slice(0, 40)}`;
            const label =
                c.label != null && String(c.label).trim() !== ""
                    ? c.label
                    : c.name != null && String(c.name).trim() !== ""
                        ? String(c.name)
                        : String(id);
            const type = normalizeMeasurementColumnType(c.type);
            const next = {
                ...c,
                id,
                label,
                type,
            };
            const w = normalizeMeasurementColumnWidthPx(c.width);
            if (w == null) {
                delete next.width;
            } else {
                next.width = w;
            }
            return next;
        });
};

/** 与 renderTestItemsTable 中 LEGACY_FT_MEASUREMENT_COLUMNS.id 一致：仅这些键时不推断、用内置表头 */
const LEGACY_FT_MEASUREMENT_ROW_KEYS = new Set([
    "SetPoint",
    "BMSValue",
    "MeasuredValue",
    "Acceptable",
    "Comment",
]);

const RESERVED_FUNCTIONAL_TEST_VALUE_KEYS = new Set(["name", "description", "type", "unit", "id"]);

/**
 * 从保存的行里收集测量列字段键（自定义列 id，如 col_*）。
 * @returns {{ kind: 'none'|'empty'|'legacy'|'custom', keys: string[] }}
 */
function extractMeasurementFieldKeysFromRows(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return { kind: "none", keys: [] };
    }
    const ids = new Set();
    for (const row of rows) {
        if (!row || typeof row !== "object") continue;
        for (const k of Object.keys(row)) {
            if (RESERVED_FUNCTIONAL_TEST_VALUE_KEYS.has(k)) continue;
            if (k.startsWith("_ft")) continue;
            ids.add(k);
        }
    }
    if (ids.size === 0) {
        return { kind: "empty", keys: [] };
    }
    const keys = [...ids];
    if (keys.every((id) => LEGACY_FT_MEASUREMENT_ROW_KEYS.has(id))) {
        return { kind: "legacy", keys };
    }
    return { kind: "custom", keys: keys.sort() };
}

/**
 * 在「无 functional_form FK」时用列 id 反查表单模板，恢复 columns[].label / type / cells。
 * 优先：列 id 集合与模板一致；其次列数接近；再其次设备分类一致。
 */
/**
 * @param {(template: object) => { rows: any[], columns: any[], cells: object }} parseTpl 与页内 parseTemplateFields 一致
 */
async function findBestFunctionalTestTemplateForMeasurementKeys(columnKeys, equipmentCategoryId, parseTpl) {
    if (!Array.isArray(columnKeys) || columnKeys.length === 0 || typeof parseTpl !== "function") return null;
    const required = new Set(columnKeys.map((k) => String(k)));
    try {
        const res = await api.filterForms("form_type=functional_test&page=1&page_size=999");
        if (res.status !== 200) return null;
        const forms = res.data.results || [];
        let best = null;
        let bestScore = -Infinity;
        for (const f of forms) {
            const parsed = parseTpl(f);
            const cols = parsed.columns || [];
            if (!cols.length) continue;
            const colIds = new Set(cols.map((c) => String(c.id)));
            let covers = true;
            for (const k of required) {
                if (!colIds.has(k)) {
                    covers = false;
                    break;
                }
            }
            if (!covers) continue;

            const usedInTemplate = cols.filter((c) => required.has(String(c.id))).length;
            const exactIdSet =
                usedInTemplate === required.size &&
                cols.length === required.size &&
                cols.every((c) => required.has(String(c.id)));
            let score = 0;
            if (exactIdSet) score += 200;
            score += 100 - Math.min(50, Math.abs(cols.length - required.size) * 5);
            if (
                equipmentCategoryId != null &&
                f.equipment_category != null &&
                String(f.equipment_category) === String(equipmentCategoryId)
            ) {
                score += 30;
            }
            if (score > bestScore) {
                bestScore = score;
                best = f;
            }
        }
        return best;
    } catch (e) {
        console.error("findBestFunctionalTestTemplateForMeasurementKeys:", e);
        return null;
    }
}

/** 与工单 PM Checklist 一致：options 可为数组或逗号分隔字符串 */
function normalizeMeasurementSelectOptionsFromFields(src) {
    const raw =
        src?.options != null && String(src.options).trim() !== ""
            ? src.options
            : src?.description;
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw.map((x) => String(x));
    if (typeof raw === "string") {
        return raw
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
    }
    return [];
}

/**
 * 类型优先级：矩阵 cells[rowId:colId] > 列 type（非 text）> 行 _ftRowType。
 * select 选项：cell.options > 列 > 行 _ftRowOptions / description。
 */
function buildEffectiveMeasurementCell(col, rowItem, cellsMap) {
    if (!col || typeof col !== "object") return col;
    const rowId =
        rowItem != null && rowItem._ftRowId != null ? String(rowItem._ftRowId) : null;
    const colId = col.id != null ? String(col.id) : null;
    const cellKey = rowId && colId ? `${rowId}:${colId}` : null;
    const cellDef = cellKey && cellsMap && typeof cellsMap === "object" ? cellsMap[cellKey] : null;

    const hasCellType =
        cellDef &&
        cellDef.type != null &&
        String(cellDef.type).trim() !== "";

    let effectiveType;
    if (hasCellType) {
        effectiveType = normalizeMeasurementColumnType(cellDef.type);
    } else {
        const colType = normalizeMeasurementColumnType(col.type);
        const rowType =
            rowItem != null &&
            rowItem._ftRowType != null &&
            String(rowItem._ftRowType).trim() !== ""
                ? normalizeMeasurementColumnType(rowItem._ftRowType)
                : "text";
        effectiveType = colType !== "text" ? colType : rowType;
    }

    let effective = {...col, type: effectiveType};
    if (
        cellDef &&
        typeof cellDef === "object" &&
        typeof cellDef.required === "boolean" &&
        cellDef.required
    ) {
        effective = {...effective, _ftCellRequired: true};
    }

    if (normalizeMeasurementColumnType(effectiveType) === "select") {
        const cellOpts =
            cellDef &&
            cellDef.options != null &&
            String(cellDef.options).trim() !== ""
                ? normalizeMeasurementSelectOptionsFromFields({options: cellDef.options})
                : [];
        const colOpts = normalizeMeasurementSelectOptionsFromFields(col);
        if (cellOpts.length) {
            effective = {...effective, options: cellDef.options, description: ""};
        } else if (colOpts.length) {
            /* keep col options/description on effective via spread from col already */
        } else if (rowItem != null) {
            effective = {
                ...effective,
                options: rowItem._ftRowOptions,
                description:
                    rowItem._ftRowOptions != null && String(rowItem._ftRowOptions).trim() !== ""
                        ? ""
                        : rowItem.description,
            };
        }
    }
    return effective;
}

const FT_MEASUREMENT_CELL_REQUIRED_MSG = "This field is required";

/**
 * 矩阵测量单元格：按模板列/行 required 生成 Ant Design rules（label/na 不校验）。
 * boolean 三态 null vs Pass/Fail；photo 校验 kpi_file_id / image。
 */
function buildFunctionalTestingMeasurementCellRules(effectiveType, cellRequired) {
    if (!cellRequired) return undefined;
    const t = normalizeMeasurementColumnType(effectiveType);
    if (t === "label" || t === "na") return undefined;
    if (t === "boolean") {
        return [
            {
                validator(_, value) {
                    if (value === true || value === false) return Promise.resolve();
                    return Promise.reject(new Error(FT_MEASUREMENT_CELL_REQUIRED_MSG));
                },
            },
        ];
    }
    if (t === "photo") {
        return [
            {
                validator(_, value) {
                    if (value && typeof value === "object" && !Array.isArray(value)) {
                        if (value.kpi_file_id != null || value.image) return Promise.resolve();
                    }
                    return Promise.reject(new Error(FT_MEASUREMENT_CELL_REQUIRED_MSG));
                },
            },
        ];
    }
    if (t === "number") {
        return [
            {
                validator(_, value) {
                    if (value !== null && value !== undefined && value !== "") return Promise.resolve();
                    return Promise.reject(new Error(FT_MEASUREMENT_CELL_REQUIRED_MSG));
                },
            },
        ];
    }
    return [{ required: true, message: FT_MEASUREMENT_CELL_REQUIRED_MSG }];
}

const FunctionalTestingDetail = ({ isAdd = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();
    const { closeTab, refreshTab } = useKeepAliveContext();
    const facilityContext = useSelector((state) => getFacilityContext(state));
    const facilityRecordCompanyId = useSelector((s) => s.Facility?.selected?.record?.company_id);
    const isPC = useSelector((state) => state.App?.isPC ?? true);
    const functionalTestingListPath = buildFacilityPath("Forms", facilityContext) || "/switch-view";
    const createEquipmentPath = buildFacilityPath("create-equipment", facilityContext) || "/switch-view";

    // Get templateId from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const templateIdParam = searchParams.get("templateId");
    /** 列表页已选 Blank 时传 templateId=blank（空字符串不能写入 query，否则创建页会再弹模板框） */
    const isBlankTemplatePreset = templateIdParam === "blank";
    const functionalFormTemplateId =
        templateIdParam && !isBlankTemplatePreset ? templateIdParam : null;
    const hasTemplateChoiceInUrl = templateIdParam != null;
    const formTypeParam = searchParams.get("formType") || "functional_test";
    const templateCategoryParam = searchParams.get("templateCategory");
    const equipmentIdParam = searchParams.get("equipmentId") || location.state?.equipmentId;
    const preselectedEquipmentId =
        equipmentIdParam !== null &&
        equipmentIdParam !== undefined &&
        equipmentIdParam !== "" &&
        !Number.isNaN(Number(equipmentIdParam))
            ? Number(equipmentIdParam)
            : equipmentIdParam;

    // Determine if this is a create page from either canonical or legacy route
    const isCreatePage = isAdd || getLeafFromPathname(location.pathname, "facility") === "create-functional-testing";

    const [loading, setLoading] = useState(isCreatePage ? false : true);
    const [readOnly, setReadOnly] = useState(!isCreatePage);
    const [testingData, setTestingData] = useState(null);
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [templateOptions, setTemplateOptions] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(() => {
        if (!isCreatePage) return null;
        if (isBlankTemplatePreset) return "";
        if (functionalFormTemplateId) return String(functionalFormTemplateId);
        return null;
    });
    /** 新建页展示用：与列表「Form type」列语义一致（含 generic → effectiveUi） */
    const [createDisplayFormType, setCreateDisplayFormType] = useState(
        () => formTypeParam || "functional_test"
    );
    const [showTemplateSelector, setShowTemplateSelector] = useState(
        isCreatePage && !hasTemplateChoiceInUrl
    );
    const [activeTabKey, setActiveTabKey] = useState("1");
    /**
     * 测量列表头来源：
     * - null — 从未根据模板同步过（沿用旧版无模板时的内置列，兼容历史记录）
     * - [] — 模板已载入且 Columns 为空，不得再注入内置 fallback（避免把 type:text 等写死塞进 UI）
     * - [...] — 模板动态列定义
     */
    const [templateMeasurementColumns, setTemplateMeasurementColumns] = useState(null);
    /** 模板 fields.cells：矩阵定义的单元格类型，键 rowId:colId */
    const [templateFieldCells, setTemplateFieldCells] = useState(null);
    /**
     * 解析模板后的 rows 元数据（含 id / required），用于行级 Required 与编辑态无 _ftRowRequired 的旧数据。
     * null — 非模板矩阵或 legacy；[] — 模板无行；否则为行快照。
     */
    const [templateRowsMeta, setTemplateRowsMeta] = useState(null);
    const [testItemsTableScrollX, setTestItemsTableScrollX] = useState(null);

    /** 列表刷新后仍要把当前选中模板塞进 options（filter 不一定包含该项） */
    const templateDropdownExtraRef = useRef(null);

    const watchedEquipment = Form.useWatch("equipment", form);
    const pathContext = extractContextFromPath(location.pathname);
    const resolvedTemplateListCompanyId =
        (facilityContext.companyId != null && String(facilityContext.companyId).trim() !== ""
            ? String(facilityContext.companyId)
            : null) ||
        (pathContext?.companyId != null && String(pathContext.companyId).trim() !== ""
            ? String(pathContext.companyId)
            : null) ||
        (facilityRecordCompanyId != null ? String(facilityRecordCompanyId) : undefined);
    /** Redux 的 Facility 可能尚未同步；规范路径 /facility/:company/:facility/... 可从 URL 解析 */
    const resolvedFacilityId =
        facilityContext?.facilityId != null && String(facilityContext.facilityId).trim() !== ""
            ? String(facilityContext.facilityId)
            : pathContext?.facilityId != null && String(pathContext.facilityId).trim() !== ""
              ? String(pathContext.facilityId)
              : null;

    const testItemsColumnWidthPersistenceKey = useMemo(() => {
        const facilityPart = resolvedFacilityId ?? "0";
        const templatePart =
            testingData?.functional_form ??
            (selectedTemplate != null && `${selectedTemplate}`.trim() !== ""
                ? selectedTemplate
                : null) ??
            (templateMeasurementColumns === null ? "legacy" : "blank");
        return `ft-test-items-col-widths:${facilityPart}:${templatePart}`;
    }, [
        resolvedFacilityId,
        testingData?.functional_form,
        selectedTemplate,
        templateMeasurementColumns,
    ]);

    useEffect(() => {
        setTestItemsTableScrollX(null);
    }, [testItemsColumnWidthPersistenceKey]);

    const equipmentForFiles =
        watchedEquipment !== undefined && watchedEquipment !== null && watchedEquipment !== ""
            ? watchedEquipment
            : testingData?.equipment ?? null;

    const userName = useSelector((state) =>
        state?.Auth?.user || state?.Auth?.first_name + " " + state?.Auth?.last_name
    );

    // Helper function to safely parse value field (API may return array or double-encoded JSON string)
    const parseValueField = (value) => {
        if (!value) return [];

        const parseJsonIfString = (raw) => {
            if (typeof raw !== "string") return raw;
            try {
                return JSON.parse(raw);
            } catch (e) {
                console.error("Error parsing value:", e);
                return null;
            }
        };

        if (typeof value === "string") {
            let parsed = parseJsonIfString(value);
            if (typeof parsed === "string") {
                parsed = parseJsonIfString(parsed);
            }
            return Array.isArray(parsed) ? parsed : [];
        }
        if (Array.isArray(value)) {
            return value;
        }

        return [];
    };

    const normalizeTemplateFields = (fields) => {
        // New {rows, columns, cells} format
        if (fields && typeof fields === "object" && !Array.isArray(fields) && fields.rows) {
            const rawCells =
                fields.cells != null && typeof fields.cells === "object" && !Array.isArray(fields.cells)
                    ? {...fields.cells}
                    : {};
            return {
                rows: fields.rows.map((row) => ({
                    id: row.id,
                    name: row.label || row.name || "",
                    description: row.description || "",
                    unit: row.unit || "",
                    type: row.type,
                    options: row.options,
                    required: Boolean(row.required),
                })),
                columns: normalizeMeasurementColumnsFromTemplate(
                    Array.isArray(fields.columns) ? fields.columns : []
                ),
                cells: rawCells,
            };
        }
        // 旧版扁平数组
        if (Array.isArray(fields)) {
            return {
                rows: fields.map((item) => ({
                    id: item.id,
                    name: item.name ?? item.label ?? "",
                    description: item.description ?? "",
                    unit: item.unit ?? "",
                    type: item.type,
                    options: item.options,
                    required: Boolean(item.required),
                })),
                columns: [],
                cells: {},
            };
        }
        return { rows: [], columns: [], cells: {} };
    };

    const parseTemplateFields = (template) => {
        if (!template?.fields) return { rows: [], columns: [], cells: {} };

        if (typeof template.fields === "string") {
            try {
                return normalizeTemplateFields(JSON.parse(template.fields));
            } catch (e) {
                console.error("Error parsing template fields:", e);
                return { rows: [], columns: [], cells: {} };
            }
        }

        return normalizeTemplateFields(template.fields);
    };

    /** 由模板生成设施端测试项；_ft* 仅前端用于渲染，提交时剥离 */
    const buildDefaultTestItems = (rows, columns, fieldCells) => {
        const cells = fieldCells && typeof fieldCells === "object" ? fieldCells : {};
        return rows.map((row) => {
            const rowTypeNorm =
                row.type != null ? normalizeMeasurementColumnType(row.type) : "text";
            const rowIdStr = row.id != null ? String(row.id) : null;
            const measurementFields = {};
            (columns || []).forEach((col) => {
                const colType = normalizeMeasurementColumnType(col?.type);
                if (colType === "label" || colType === "na") return;
                const cellKey =
                    rowIdStr && col.id != null ? `${rowIdStr}:${String(col.id)}` : null;
                const cellDef = cellKey ? cells[cellKey] : null;
                const hasCellType =
                    cellDef &&
                    cellDef.type != null &&
                    String(cellDef.type).trim() !== "";
                let effectiveType;
                if (hasCellType) {
                    effectiveType = normalizeMeasurementColumnType(cellDef.type);
                } else {
                    effectiveType = colType !== "text" ? colType : rowTypeNorm;
                }
                measurementFields[col.id] =
                    effectiveType === "boolean"
                        ? null
                        : effectiveType === "photo"
                          ? null
                          : "";
            });
            const rowExtras =
                rowTypeNorm !== "text"
                    ? {_ftRowType: rowTypeNorm}
                    : {};
            const optExtras =
                row.options != null && String(row.options).trim() !== ""
                    ? {_ftRowOptions: row.options}
                    : rowTypeNorm === "select" &&
                        row.description &&
                        String(row.description).trim() !== ""
                      ? {_ftRowOptions: row.description}
                      : {};
            const idExtra =
                row.id != null ? {_ftRowId: row.id} : {};
            const reqExtra = row.required ? {_ftRowRequired: true} : {};
            return {
                name: row.name || row.label || "",
                description: row.description || "",
                unit: row.unit || "",
                ...idExtra,
                ...reqExtra,
                ...rowExtras,
                ...optExtras,
                ...measurementFields,
            };
        });
    };

    const applyParsedTemplateToForm = ({
        rows,
        columns,
        fieldCells,
        templateRecord,
        formTypeOverride,
    }) => {
        const fc = fieldCells && typeof fieldCells === "object" ? fieldCells : {};
        const defaultValues = buildDefaultTestItems(rows, columns, fc);
        form.setFieldsValue({ value: defaultValues });
        setTemplateMeasurementColumns(columns);
        setTemplateFieldCells(fc);
        setTemplateRowsMeta(Array.isArray(rows) ? rows : []);
        const nextFt =
            formTypeOverride != null && String(formTypeOverride).trim() !== ""
                ? String(formTypeOverride).trim().toLowerCase()
                : templateRecord &&
                    (effectiveUiFormType(templateRecord) || templateRecord.form_type);
        setCreateDisplayFormType(nextFt ? String(nextFt) : formTypeParam || "functional_test");
        if (templateRecord?.id != null) {
            setSelectedTemplate(String(templateRecord.id));
        }
    };

    const loadBlankTemplateIntoCreateForm = () => {
        templateDropdownExtraRef.current = null;
        const blank = templateOptions.find((t) => t.id === "") || { fields: [] };
        const { rows, columns, cells: fieldCells } = parseTemplateFields(blank);
        applyParsedTemplateToForm({
            rows,
            columns,
            fieldCells,
            templateRecord: null,
            formTypeOverride: formTypeParam || "functional_test",
        });
        setSelectedTemplate("");
    };

    const loadTemplateRecordIntoCreateForm = (template, notifySuccess = true) => {
        templateDropdownExtraRef.current = template;
        const { rows, columns, cells: fieldCells } = parseTemplateFields(template);
        applyParsedTemplateToForm({
            rows,
            columns,
            fieldCells,
            templateRecord: template,
            formTypeOverride: null,
        });
        if (notifySuccess && template?.id != null) {
            globalMessage.success("Template loaded successfully");
        }
        setTemplateOptions((prev) =>
            mergeTemplateRecordIntoTemplateOptionsMerge(
                Array.isArray(prev) && prev.length > 0
                    ? prev
                    : [{ id: "", name: "Blank Template (Default)", fields: [] }],
                template
            )
        );
    };

    const handleCreateTemplateSelectChange = async (val) => {
        const v = val !== undefined && val !== null ? val : "";
        if (v === "" || String(v).trim() === "") {
            loadBlankTemplateIntoCreateForm();
            return;
        }
        try {
            setLoading(true);
            const response = await api.getFormById(String(v));
            const template = response.data?.results?.[0];
            if (response.status === 200 && template) {
                loadTemplateRecordIntoCreateForm(template, true);
            } else {
                globalMessage.error("Failed to load template");
            }
        } catch (e) {
            console.error("Error loading template:", e);
            globalMessage.error("Failed to load template");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isCreatePage && userName) {
            const current = form.getFieldValue("record_by");
            if (current == null || String(current).trim() === "") {
                form.setFieldsValue({ record_by: userName });
            }
        }
    }, [isCreatePage, userName]);

    useEffect(() => {
        if (!isCreatePage) return;
        const current = form.getFieldValue("record_date");
        if (current == null || current === "") {
            form.setFieldsValue({ record_date: moment() });
        }
    }, [isCreatePage]);

    useEffect(() => {
        if (isCreatePage) {
            (async () => {
                fetchEquipmentList(preselectedEquipmentId);
                await fetchTemplateList();
                if (isBlankTemplatePreset) {
                    loadBlankTemplateIntoCreateForm();
                } else if (functionalFormTemplateId) {
                    fetchTemplateById(functionalFormTemplateId);
                }
            })();
        } else if (id) {
            fetchEquipmentList();
            (async () => {
                await fetchTestingDetail();
                await fetchTemplateList();
            })();
        }
    }, [
        id,
        isCreatePage,
        templateIdParam,
        preselectedEquipmentId,
        formTypeParam,
        templateCategoryParam,
        resolvedTemplateListCompanyId,
    ]);

    const fetchTestingDetail = async (options = {}) => {
        const { skipLoading = false } = options;
        try {
            if (!skipLoading) {
                setLoading(true);
            }
            const response = await api.filterFunctionalTestingView(`id=${id}`);

            if (response.status === 200 && response.data.results.length > 0) {
                const data = response.data.results[0];

                const valueData = parseValueField(data.value);

                let templateResolved = false;
                let columnsToSet = null;
                let cellsToSet = null;
                let rowsMetaToSet = null;
                let resolvedTemplateSnapshot = null;
                let linkedFormIdForUi =
                    data.functional_form != null && `${data.functional_form}`.trim() !== ""
                        ? data.functional_form
                        : null;

                if (data.functional_form != null && `${data.functional_form}`.trim() !== "") {
                    try {
                        const formRes = await api.getFormById(data.functional_form);
                        const template = formRes.data?.results?.[0];
                        if (formRes.status === 200 && template) {
                            resolvedTemplateSnapshot = template;
                            const parsed = parseTemplateFields(template);
                            columnsToSet = parsed.columns;
                            cellsToSet = parsed.cells || {};
                            rowsMetaToSet = Array.isArray(parsed.rows) ? parsed.rows : [];
                            templateResolved = true;
                        }
                    } catch (e) {
                        console.error("Failed to load functional form template:", e);
                    }
                }
                if (!templateResolved) {
                    const { kind, keys } = extractMeasurementFieldKeysFromRows(valueData);
                    if (kind === "custom" && keys.length > 0) {
                        let equipmentCategoryId = null;
                        if (data.equipment != null && `${data.equipment}`.trim() !== "") {
                            try {
                                const er = await api.filterEquipment(
                                    `id=${data.equipment}&page=1&page_size=1`
                                );
                                const eq = er.data?.results?.[0];
                                equipmentCategoryId =
                                    eq?.equipment_category != null ? eq.equipment_category : null;
                            } catch (_) {
                                /* ignore */
                            }
                        }
                        const matchedForm = await findBestFunctionalTestTemplateForMeasurementKeys(
                            keys,
                            equipmentCategoryId,
                            parseTemplateFields
                        );
                        if (matchedForm) {
                            if (!resolvedTemplateSnapshot) resolvedTemplateSnapshot = matchedForm;
                            const parsed = parseTemplateFields(matchedForm);
                            columnsToSet = parsed.columns;
                            cellsToSet = parsed.cells || {};
                            rowsMetaToSet = Array.isArray(parsed.rows) ? parsed.rows : [];
                            linkedFormIdForUi = matchedForm.id;
                        } else {
                            columnsToSet = normalizeMeasurementColumnsFromTemplate(
                                keys.map((id) => ({ id, label: id, type: "text" }))
                            );
                            cellsToSet = {};
                            rowsMetaToSet = null;
                        }
                    } else if (kind === "empty") {
                        columnsToSet = [];
                        cellsToSet = {};
                        rowsMetaToSet = [];
                    } else {
                        columnsToSet = null;
                        cellsToSet = null;
                        rowsMetaToSet = null;
                    }
                }
                setTemplateRowsMeta(rowsMetaToSet);
                setTemplateMeasurementColumns(columnsToSet);
                setTemplateFieldCells(cellsToSet);

                let displayFt = "";
                if (resolvedTemplateSnapshot) {
                    displayFt =
                        effectiveUiFormType(resolvedTemplateSnapshot) ||
                        String(resolvedTemplateSnapshot.form_type || "").trim().toLowerCase();
                } else if (
                    data.functional_form__form_type != null &&
                    `${data.functional_form__form_type}`.trim() !== ""
                ) {
                    displayFt = String(data.functional_form__form_type).trim().toLowerCase();
                } else if (data.form_type != null && `${data.form_type}`.trim() !== "") {
                    displayFt = String(data.form_type).trim().toLowerCase();
                }
                setCreateDisplayFormType(displayFt);

                templateDropdownExtraRef.current = resolvedTemplateSnapshot || null;

                setSelectedTemplate(
                    linkedFormIdForUi != null && `${linkedFormIdForUi}`.trim() !== ""
                        ? String(linkedFormIdForUi)
                        : "",
                );

                const enrichedTestingPayload = {
                    ...data,
                    ...(resolvedTemplateSnapshot
                        ? {
                              functional_form_name:
                                  resolvedTemplateSnapshot.name ?? data.functional_form_name,
                              functional_form__form_type:
                                  resolvedTemplateSnapshot.form_type ??
                                  data.functional_form__form_type,
                          }
                        : {}),
                };
                setTestingData(enrichedTestingPayload);

                const formValues = {
                    ...enrichedTestingPayload,
                    record_date: data.record_date ? moment(data.record_date) : null,
                    value: valueData,
                };
                form.setFieldsValue(formValues);
            } else {
                globalMessage.error("Functional Testing not found");
            }
        } catch (error) {
            console.error("Error fetching functional testing:", error);
                globalMessage.error("Failed to load functional testing details");
        } finally {
            if (!skipLoading) {
                setLoading(false);
            }
        }
    };

    const fetchEquipmentList = async (defaultEquipmentId) => {
        try {
            const response = await api.filterEquipment("page=1&page_size=999");
            if (response.status === 200) {
                const equipmentList = response.data.results || [];
                setEquipmentOptions(equipmentList);

                if (
                    defaultEquipmentId !== null &&
                    defaultEquipmentId !== undefined &&
                    defaultEquipmentId !== ""
                ) {
                    const matchedEquipment = equipmentList.find(
                        (equipment) => `${equipment.id}` === `${defaultEquipmentId}`
                    );

                    form.setFieldsValue({
                        equipment: matchedEquipment ? matchedEquipment.id : defaultEquipmentId,
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching equipment list:", error);
        }
    };

    const fetchTemplateList = async () => {
        try {
            const response = await filterFormsByFormType({
                formType: formTypeParam || "functional_test",
                equipmentType:
                    templateCategoryParam != null && String(templateCategoryParam).trim() !== ""
                        ? templateCategoryParam
                        : undefined,
                companyId: resolvedTemplateListCompanyId,
            });
            if (response.status === 200) {
                const templates = response.data.results || [];
                const base = [
                    { id: "", name: "Blank Template (Default)", fields: [] },
                    ...templates,
                ];
                const extra = templateDropdownExtraRef.current;
                setTemplateOptions(mergeTemplateRecordIntoTemplateOptionsMerge(base, extra));
            }
        } catch (error) {
            console.error("Error fetching template list:", error);
            const data = error?.response?.data;
            const serverMsg =
                (typeof data?.detail === "string" ? data.detail : null) ||
                (Array.isArray(data?.form_type) ? data.form_type.join(" ") : null);
            globalMessage.error(
                serverMsg
                    ? `Failed to load template list: ${serverMsg}`
                    : "Failed to load template list"
            );
        }
    };

    const fetchTemplateById = async (tid) => {
        try {
            setLoading(true);
            const response = await api.getFormById(String(tid));

            if (response.status === 200) {
                const template = response.data?.results?.[0];
                if (template) {
                    loadTemplateRecordIntoCreateForm(template, true);
                }
            }
        } catch (error) {
            console.error("Error fetching template:", error);
                globalMessage.error("Failed to load template");
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateSelect = (templateId) => {
        if (templateId === "" || templateId === null || templateId === undefined) {
            setSelectedTemplate("");
            return;
        }
        setSelectedTemplate(String(templateId));
    };

    const getEquipmentDisplayName = () => {
        const equipmentId = form.getFieldValue("equipment") || testingData?.equipment;
        const matchedEquipment = equipmentOptions.find(
            (equipment) => `${equipment.id}` === `${equipmentId}`
        );

        return testingData?.equipment__name || matchedEquipment?.name || "";
    };

    const handleTemplateConfirm = async () => {
        if (selectedTemplate === null || selectedTemplate === undefined) {
                globalMessage.warning("Please select a template");
            return;
        }
        const isBlank = selectedTemplate === "";
        try {
            if (!isBlank) {
                setLoading(true);
                const response = await api.getFormById(String(selectedTemplate));
                const template = response.data?.results?.[0];
                if (response.status !== 200 || !template) {
                    globalMessage.error("Failed to load template");
                    return;
                }
                loadTemplateRecordIntoCreateForm(template, false);
                globalMessage.success("Template loaded successfully");
            } else {
                loadBlankTemplateIntoCreateForm();
            }
            setShowTemplateSelector(false);
        } catch (error) {
            console.error("Error loading template:", error);
            globalMessage.error("Failed to load template");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        setReadOnly(false);
    };

    const handleCancel = () => {
        setReadOnly(true);
        if (!isCreatePage && id) {
            fetchTestingDetail({ skipLoading: true });
            return;
        }
        if (testingData) {
            // Parse the value field - handle both string and object
            const valueData = parseValueField(testingData.value);

            const formValues = {
                ...testingData,
                record_date: testingData.record_date ? moment(testingData.record_date) : null,
                value: valueData,
            };
            form.setFieldsValue(formValues);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            // 套用 Functional Form 模板时（templateMeasurementColumns 已同步），不写设施端遗留的「Type」字段
            let valueForSubmit = Array.isArray(values.value) ? values.value : [];
            if (templateMeasurementColumns !== null) {
                valueForSubmit = valueForSubmit.map((row) => {
                    if (row == null || typeof row !== "object") return row;
                    const { type: _omitType, ...rest } = row;
                    const cleaned = {};
                    Object.keys(rest).forEach((k) => {
                        if (String(k).startsWith("_ft")) return;
                        cleaned[k] = rest[k];
                    });
                    return cleaned;
                });
            }

            const selectedTemplateForSubmit =
                selectedTemplate != null && selectedTemplate !== "" && selectedTemplate !== undefined
                    ? selectedTemplate
                    : functionalFormTemplateId != null && `${functionalFormTemplateId}`.trim() !== ""
                      ? functionalFormTemplateId
                      : testingData?.functional_form != null && `${testingData.functional_form}`.trim() !== ""
                        ? testingData.functional_form
                        : null;

            const recordDate = values.record_date?.format
                ? values.record_date.format("YYYY-MM-DD HH:mm:ss")
                : values.record_date;
            const formType =
                createDisplayFormType != null && String(createDisplayFormType).trim() !== ""
                    ? String(createDisplayFormType).trim().toLowerCase()
                    : formTypeParam || "functional_test";
            const testDate = values.record_date?.format
                ? values.record_date.format("YYYY-MM-DD")
                : moment(values.record_date).format("YYYY-MM-DD");
            const hasFormName = values.name != null && String(values.name).trim() !== "";

            // Format values
            const submitValues = {
                ...values,
                name:
                    isCreatePage && !hasFormName
                        ? [getEquipmentDisplayName(), formType, testDate].join("_")
                        : values.name,
                record_date: recordDate,
                form_type: formType,
                value: valueForSubmit,
                record_by:
                    values.record_by != null && String(values.record_by).trim() !== ""
                        ? String(values.record_by).trim()
                        : userName,
                functional_form: selectedTemplateForSubmit,
            };

            let response;
            if (isCreatePage) {
                // Create mode
                response = await api.createFunctionalTestingView(submitValues);
            } else {
                // Edit mode
                submitValues.modifiedby_name = userName;
                response = await api.editFunctionalTestingView(id, submitValues);
            }

            if (response.status === 200 || response.status === 201) {
                globalMessage.success({
                    message: "Success",
                    description: isCreatePage
                        ? "Functional Testing created successfully"
                        : "Functional Testing updated successfully",
                });

                if (isCreatePage) {
                    refreshTab(functionalTestingListPath);
                    closeTab();
                    navigate(functionalTestingListPath);
                } else {
                    setReadOnly(true);
                    fetchTestingDetail();
                }
            } else {
                globalMessage.error({
                    message: "Error",
                    description: isCreatePage
                        ? "Failed to create functional testing"
                        : "Failed to update functional testing",
                });
            }
        } catch (error) {
            console.error(`Error ${isCreatePage ? "creating" : "updating"} functional testing:`, error);
            globalMessage.error({
                message: "Error",
                description: isCreatePage
                    ? "Failed to create functional testing"
                    : "Failed to update functional testing",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        Modal.confirm({
            title: "Are you sure you want to delete this functional testing?",
            content: "This action cannot be undone.",
            okText: "Yes",
            okType: "danger",
            cancelText: "No",
            onOk: async () => {
                try {
                    setLoading(true);
                    const response = await api.deleteFunctionalTestingView(id);

                    if (response.status === 204 || response.status === 200) {
                        globalMessage.success({
                            message: "Success",
                            description: "Functional Testing deleted successfully",
                        });
                        refreshTab(functionalTestingListPath);
                        closeTab();
                        navigate(functionalTestingListPath);
                    } else {
                        globalMessage.error({
                            message: "Error",
                            description: "Failed to delete functional testing",
                        });
                    }
                } catch (error) {
                    console.error("Error deleting functional testing:", error);
                    globalMessage.error({
                        message: "Error",
                        description: "Failed to delete functional testing",
                    });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleCancelCreate = () => {
        closeTab();
        navigate(functionalTestingListPath);
    };

    const renderOperationButton = () => {
        if (isCreatePage) {
            return (
                <>
                    <Button onClick={handleCancelCreate}>
                        <i className="fa-solid fa-xmark"></i>Cancel
                    </Button>
                    <Button type="primary" onClick={() => form.submit()}>
                        <i className="fa-solid fa-floppy-disk"></i>Save
                    </Button>
                </>
            );
        }

        if (readOnly) {
            return (
                <>
                    <Button type="primary" onClick={handleEdit}>
                        <i className="fa-solid fa-pen-to-square"></i>Edit
                    </Button>
                    <Button danger onClick={handleDelete}>
                        <i className="fa-solid fa-trash"></i>Delete
                    </Button>
                </>
            );
        } else {
            return (
                <>
                    <Button type="primary" onClick={() => form.submit()}>
                        <i className="fa-solid fa-floppy-disk"></i>Save
                    </Button>
                    <Button onClick={handleCancel}>
                        Cancel
                    </Button>
                </>
            );
        }
    };

    const safeRenderValue = (val) => {
        if (val === null || val === undefined) return "";
        if (typeof val === "boolean") return val ? "Yes" : "No";
        if (typeof val === "object") return JSON.stringify(val);
        return val;
    };

    const renderReadOnlyCell = (value, { multiline = false } = {}) => {
        const text = safeRenderValue(value);
        if (text === "") {
            return <span className="functional-testing-items-cell-readonly functional-testing-items-cell-readonly--empty">—</span>;
        }
        const className = multiline
            ? "functional-testing-items-cell-readonly functional-testing-items-cell-readonly--wrap"
            : "functional-testing-items-cell-readonly";
        return (
            <Tooltip title={text} placement="topLeft">
                <span className={className}>{text}</span>
            </Tooltip>
        );
    };

    // Map column type to input component
    /** 表格外层 Form 为 horizontal + labelCol/wrapperCol，子项会继承导致控件只占一截；表格内强制占满单元格 */
    const testItemCellFormItemLayout = {
        style: { marginBottom: 0 },
        labelCol: { span: 0 },
        wrapperCol: { span: 24 },
    };

    const renderMeasurementInput = (col, fieldName, isReadOnly, rules) => {
        const t = normalizeMeasurementColumnType(col?.type);
        const fullWidth = { width: "100%" };
        switch (t) {
            case "number":
                return (
                    <InputNumber
                        disabled={isReadOnly}
                        controls={false}
                        style={fullWidth}
                    />
                );
            case "boolean":
                return (
                    <Form.Item {...testItemCellFormItemLayout} name={fieldName} rules={rules}>
                        <PassFailToggleButtons readOnly={isReadOnly} />
                    </Form.Item>
                );
            case "select": {
                const opts = normalizeMeasurementSelectOptionsFromFields(col);
                return (
                    <Select
                        allowClear
                        disabled={isReadOnly}
                        style={fullWidth}
                        placeholder="Select"
                    >
                        {opts.map((opt) => (
                            <Option key={String(opt)} value={opt}>
                                {String(opt)}
                            </Option>
                        ))}
                    </Select>
                );
            }
            case "textarea":
                return (
                    <TextArea
                        rows={2}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        disabled={isReadOnly}
                        style={fullWidth}
                    />
                );
            case "date":
                return (
                    <CustomDatePicker
                        format="YYYY-MM-DD"
                        style={fullWidth}
                        disabled={isReadOnly}
                    />
                );
            case "label":
                return <span style={{ color: "#8c8c8c" }}>—</span>;
            case "na":
                return (
                    <div
                        className="functional-testing-measurement-cell-na"
                        title="Not applicable (template)"
                    >
                        <span className="functional-testing-measurement-cell-na__label">N/A</span>
                    </div>
                );
            default:
                return <Input disabled={isReadOnly} style={fullWidth} />;
        }
    };

    // Render dynamic test items table using Form.List
    const renderTestItemsTable = () => {
        const LEGACY_FT_MEASUREMENT_COLUMNS = [
            { id: "SetPoint", label: "Set Point", type: "text" },
            { id: "BMSValue", label: "BMS Value", type: "text" },
            { id: "MeasuredValue", label: "Measured Value", type: "text" },
            { id: "Acceptable", label: "Acceptable", type: "boolean" },
            { id: "Comment", label: "Comment", type: "textarea" },
        ];
        /** null = 从未套用模板快照，沿用 LEGACY；已套用模板时即使 [] 也不得回退到内置列 */
        const measurementCols =
            templateMeasurementColumns === null ? LEGACY_FT_MEASUREMENT_COLUMNS : templateMeasurementColumns;

        const measurementColsTotalWidthPx = measurementCols.reduce(
            (sum, col) => sum + measurementColumnTableWidthPx(col),
            0
        );

        /**
         * 第一列 「Type」 是旧版手填字段，并非模板 columns[].type。
         * 已从模板载入行/列快照时一律隐藏该列（模板未声明设施端「Type」列）。
         */
        const showLegacyFacilityTypeColumn = templateMeasurementColumns === null;

        const newItemDefaults = () => {
            const base = {
                name: "",
                description: "",
                ...(templateMeasurementColumns !== null ? {unit: ""} : {}),
            };
            measurementCols.forEach((col) => {
                const t = normalizeMeasurementColumnType(col?.type);
                if (t === "label" || t === "na") return;
                base[col.id] = t === "boolean" ? null : t === "photo" ? null : "";
            });
            return base;
        };

        // Mobile: render cards instead of table
        if (!isPC) {
            return (
                <Form.List name="value">
                    {(fields, { add, remove }) => (
                        <MobileTestItemsCardList
                            fields={fields}
                            add={add}
                            remove={remove}
                            form={form}
                            readOnly={readOnly}
                            measurementCols={measurementCols}
                            templateMeasurementColumns={templateMeasurementColumns}
                            templateFieldCells={templateFieldCells}
                            templateRowsMeta={templateRowsMeta}
                            showLegacyFacilityTypeColumn={showLegacyFacilityTypeColumn}
                            equipmentForFiles={equipmentForFiles}
                            resolvedFacilityId={resolvedFacilityId}
                            id={id}
                            isCreatePage={isCreatePage}
                            newItemDefaults={newItemDefaults}
                            buildEffectiveMeasurementCell={buildEffectiveMeasurementCell}
                            normalizeMeasurementColumnType={normalizeMeasurementColumnType}
                            normalizeMeasurementSelectOptionsFromFields={normalizeMeasurementSelectOptionsFromFields}
                            buildFunctionalTestingMeasurementCellRules={buildFunctionalTestingMeasurementCellRules}
                            FunctionalTestingPhotoFormControl={FunctionalTestingPhotoFormControl}
                            PassFailToggleButtons={PassFailToggleButtons}
                        />
                    )}
                </Form.List>
            );
        }

        const facilityTypeColumn = {
                key: "legacy_type",
                title: "Type",
                width: 120,
                render: (_, field) => (
                    <Form.Item {...testItemCellFormItemLayout} name={[field.name, "type"]}>
                        {readOnly ? (
                            renderReadOnlyCell(form.getFieldValue(["value", field.name, "type"]))
                        ) : (
                            <Input disabled={readOnly} style={{ width: "100%" }} />
                        )}
                    </Form.Item>
                ),
        };

        const getTableColumns = (remove) => [
            ...(showLegacyFacilityTypeColumn ? [facilityTypeColumn] : []),
            {
                key: "name",
                title: "Name",
                width: 140,
                render: (_, field) => (
                    <Form.Item {...testItemCellFormItemLayout} name={[field.name, "name"]}>
                        {readOnly ? (
                            renderReadOnlyCell(form.getFieldValue(["value", field.name, "name"]))
                        ) : (
                            <Input disabled={readOnly} style={{ width: "100%" }} />
                        )}
                    </Form.Item>
                ),
            },
            {
                key: "description",
                title: "Description",
                width: 220,
                render: (_, field) => (
                    <Form.Item {...testItemCellFormItemLayout} name={[field.name, "description"]}>
                        {readOnly ? (
                            renderReadOnlyCell(form.getFieldValue(["value", field.name, "description"]), {
                                multiline: true,
                            })
                        ) : (
                            <Input style={{ width: "100%" }} />
                        )}
                    </Form.Item>
                ),
            },
            ...(templateMeasurementColumns !== null
                ? [
                      {
                          key: "unit",
                          title: "Unit",
                          width: 72,
                          render: (_, field) => (
                              <Form.Item {...testItemCellFormItemLayout} name={[field.name, "unit"]}>
                                  {readOnly ? (
                                      renderReadOnlyCell(
                                          form.getFieldValue(["value", field.name, "unit"]) || "—"
                                      )
                                  ) : (
                                      <Input placeholder="e.g. °F, %" style={{ width: "100%" }} />
                                  )}
                              </Form.Item>
                          ),
                      },
                  ]
                : []),
            // Dynamic measurement columns from template (or legacy fallback row)
            ...measurementCols.map((col) => ({
                key: String(col.id),
                title: (
                    <span>
                        {col.label ?? col.id}
                        {col.required ? (
                            <span style={{ color: "#ff4d4f", marginLeft: 2 }} aria-hidden>
                                *
                            </span>
                        ) : null}
                    </span>
                ),
                width: measurementColumnTableWidthPx(col),
                render: (_, field) => {
                    const rowItem = form.getFieldValue(["value", field.name]) || {};
                    const effectiveCol = buildEffectiveMeasurementCell(
                        col,
                        rowItem,
                        templateFieldCells || {}
                    );
                    const effType = normalizeMeasurementColumnType(effectiveCol.type);
                    const isBool = effType === "boolean";
                    const isPhoto = effType === "photo";
                    const isLabelCol = effType === "label";
                    const isNaCol = effType === "na";
                    const rowMeta =
                        templateRowsMeta != null
                            ? templateRowsMeta.find((r) => String(r.id) === String(rowItem._ftRowId))
                            : null;
                    const rowRequired = !!(rowItem._ftRowRequired || rowMeta?.required);
                    const cellLevelReq = !!effectiveCol._ftCellRequired;
                    const cellRequired =
                        !readOnly &&
                        !isLabelCol &&
                        !isNaCol &&
                        (Boolean(col.required) || rowRequired || cellLevelReq);
                    const cellRules = buildFunctionalTestingMeasurementCellRules(effType, cellRequired);
                    if (isLabelCol || isNaCol) {
                        return renderMeasurementInput(effectiveCol, [field.name, col.id], readOnly);
                    }
                    if (isBool) {
                        return renderMeasurementInput(
                            effectiveCol,
                            [field.name, col.id],
                            readOnly,
                            cellRules
                        );
                    }
                    if (isPhoto) {
                        return (
                            <Form.Item
                                {...testItemCellFormItemLayout}
                                name={[field.name, col.id]}
                                rules={cellRules}
                            >
                                <FunctionalTestingPhotoFormControl
                                    readOnly={readOnly}
                                    equipmentId={equipmentForFiles}
                                    facilityId={resolvedFacilityId}
                                    functionalTestingRecordId={isCreatePage ? null : id}
                                />
                            </Form.Item>
                        );
                    }
                    return (
                        <Form.Item
                            {...testItemCellFormItemLayout}
                            name={[field.name, col.id]}
                            rules={cellRules}
                        >
                            {readOnly ? (
                                renderReadOnlyCell(form.getFieldValue(["value", field.name, col.id]), {
                                    multiline:
                                        normalizeMeasurementColumnType(effectiveCol.type) === "textarea",
                                })
                            ) : (
                                renderMeasurementInput(effectiveCol, [field.name, col.id], readOnly)
                            )}
                        </Form.Item>
                    );
                },
            })),
            {
                key: "_actions",
                title: "",
                width: 48,
                align: "center",
                render: (_, field) =>
                    !readOnly && (
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                        />
                    ),
            },
        ];

        const staticColumnsWidthPx =
            (showLegacyFacilityTypeColumn ? 120 : 0) + 140 + 220 + (templateMeasurementColumns !== null ? 72 : 0) + 48;
        const defaultTableScrollX = Math.max(960, measurementColsTotalWidthPx + staticColumnsWidthPx);

        const handleTestItemsColumnWidthsChange = (widths) => {
            let total = 0;
            getTableColumns(() => {}).forEach((col) => {
                const k = col.key != null ? String(col.key) : "";
                total += widths[k] ?? col.width ?? 120;
            });
            setTestItemsTableScrollX(Math.max(960, total));
        };

        return (
            <Form.List name="value">
                {(fields, { add, remove }) => (
                    <>
                        <div className="functional-testing-items-table-wrap">
                            <ResizableTable
                                className="functional-testing-items-table"
                                tableLayout="fixed"
                                dataSource={fields}
                                pagination={false}
                                rowKey="key"
                                size="small"
                                style={{ width: "100%" }}
                                columnWidthPersistenceKey={testItemsColumnWidthPersistenceKey}
                                onColumnWidthsChange={handleTestItemsColumnWidthsChange}
                                scroll={{
                                    x: testItemsTableScrollX ?? defaultTableScrollX,
                                    y: FT_ITEMS_TABLE_BODY_MAX_HEIGHT_PX,
                                }}
                                columns={getTableColumns(remove)}
                            />
                        </div>
                        {!readOnly && (
                            <div style={{ marginBottom: 16 }}>
                                <Button
                                    type="dashed"
                                    onClick={() => add(newItemDefaults())}
                                    block
                                    icon={<PlusOutlined />}
                                >
                                    Add Test Item
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Form.List>
        );
    };

    // Render form fields
    const renderFormFields = () => {
        return (
            <Form
                form={form}
                onFinish={handleSubmit}
                scrollToFirstError
                layout="horizontal"
                labelCol={{ span: DETAIL_FORM_LABEL_SPAN }}
                wrapperCol={{ span: DETAIL_FORM_WRAPPER_SPAN }}
                className="detailForm"
                style={{
                    "--detail-form-label-span": DETAIL_FORM_LABEL_SPAN,
                    "--detail-form-wrapper-span": DETAIL_FORM_WRAPPER_SPAN,
                }}
                        autoComplete="off">
                <Collapse
                    className="functional-testing-details-collapse"
                    defaultActiveKey={["details"]}
                >
                    <Panel header="Details" key="details">
                        <Row gutter={[0, 0]}>
                    {/* ID - Hidden in create mode */}
                    {!isCreatePage && (
                        <>
                            <Col xs={24} lg={12}>
                                <Form.Item label="ID" name="id">
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                            <Col xs={0} lg={12} />
                        </>
                    )}

                    {/* Equipment */}
                    <Col xs={24} lg={12}>
                        {readOnly ? (
                            <Form.Item label="Equipment">
                                <Input disabled value={getEquipmentDisplayName()} />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label="Equipment"
                                name="equipment"
                                rules={[{ required: true, message: "This field is required" }]}
                            >
                                <Select
                                    disabled={readOnly}
                                    placeholder="Select Equipment"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.children ?? "")
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                    dropdownRender={(menu) => (
                                        <>
                                            {menu}
                                             <div
                                                 style={{
                                                     borderTop: '1px solid #f0f0f0',
                                                     padding: '8px',
                                                     cursor: 'pointer',
                                                 }}
                                             >
                                                 <Button
                                                     type="primary"
                                                     icon={<i className="fa-solid fa-plus pr-1"></i>}
                                                     onClick={() => {
                                                         navigate(createEquipmentPath);
                                                     }}
                                                     style={{ width: '100%', textAlign: 'left' }}
                                                 >
                                                     New Equipment
                                                 </Button>
                                             </div>
                                        </>
                                    )}
                                >
                                    {equipmentOptions.map((eq) => (
                                        <Option key={eq.id} value={eq.id}>
                                            {eq.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </Col>

                    {/* Record name (optional) */}
                    <Col xs={24} lg={12}>
                        <Form.Item label="Form name" name="name">
                            <Input
                                disabled={readOnly}
                                readOnly={readOnly}
                                placeholder={readOnly ? undefined : "Optional"}
                                autoComplete="off"
                                allowClear={!readOnly}
                                maxLength={500}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                        <Form.Item label="Form type">
                            <span>{formTypeChoiceLabel(createDisplayFormType)}</span>
                        </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Form.Item label="Template">
                            {isCreatePage ? (
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    optionLabelProp="children"
                                    style={{ width: "100%" }}
                                    placeholder={
                                        templateOptions.length
                                            ? "Select template"
                                            : "Loading templates…"
                                    }
                                    value={
                                        selectedTemplate === undefined
                                            ? undefined
                                            : selectedTemplate === null ||
                                                selectedTemplate === ""
                                              ? ""
                                              : selectedTemplate
                                    }
                                    onChange={handleCreateTemplateSelectChange}
                                    getPopupContainer={(trigger) =>
                                        trigger.parentElement || document.body
                                    }
                                    disabled={Boolean(showTemplateSelector)}
                                >
                                    {templateOptions.map((t) => (
                                        <Option
                                            key={t.id === "" ? "blank" : String(t.id)}
                                            value={t.id === "" ? "" : String(t.id)}
                                            label={formTemplateOptionSearchText(t)}
                                            displayLabel={
                                                t.id === ""
                                                    ? "Blank Template (Default)"
                                                    : getFormTemplateSelectLabel(t)
                                            }
                                        >
                                            <FormTemplateOptionLabel row={t} />
                                        </Option>
                                    ))}
                                </Select>
                            ) : (
                                <span>
                                    {testingData?.functional_form != null &&
                                    `${testingData.functional_form}`.trim() !== "" &&
                                    testingData?.functional_form_name != null &&
                                    String(testingData.functional_form_name).trim() !== ""
                                        ? testingData.functional_form_name
                                        : testingData?.functional_form != null &&
                                            `${testingData.functional_form}`.trim() !== ""
                                          ? `#${testingData.functional_form}`
                                          : "—"}
                                </span>
                            )}
                        </Form.Item>
                    </Col>

                    {/* Test Date */}
                    <Col xs={24} lg={12}>
                        <Form.Item
                            label="Test Date"
                            name="record_date"
                            rules={[{ required: true, message: "This field is required" }]}
                        >
                            <CustomDatePicker
                                format="YYYY-MM-DD"
                                style={{ width: "100%" }}
                                disabled={readOnly}
                            />
                        </Form.Item>
                    </Col>

                    {/* Test By — default current user; editable */}
                    <Col xs={24} lg={12}>
                        <Form.Item label="Test By" name="record_by">
                            <Input disabled={readOnly} autoComplete="off" allowClear />
                        </Form.Item>
                    </Col>

                    {/* Description - Full Width */}
                    <Col xs={24} lg={12}>
                        <Form.Item
                            label="Summary"
                            name="description"
                        >
                            <TextArea rows={3} disabled={readOnly} maxLength={255} showCount />
                        </Form.Item>
                    </Col>
                        </Row>
                    </Panel>
                </Collapse>
                <div className="functional-testing-test-items-block">
                    <div className="functional-testing-items-section-title">Test items</div>
                    {renderTestItemsTable()}
                </div>
                {/* Audit Fields - Below Test Items */}
                {!isCreatePage && (
                    <Collapse
                        className="functional-testing-audit-collapse"
                        defaultActiveKey={[]}
                        ghost
                    >
                        <Panel header="Audit Info" key="audit">
                            <Row gutter={[16, 0]}>
                                <Col xs={12} lg={12}>
                                    <Form.Item label="Created By" name="createdby_name">
                                        <span>{testingData?.createdby_name}</span>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} lg={12}>
                                    <Form.Item label="Created Date" name="created_date">
                                        <span>
                                            {testingData?.created_date
                                                ? moment(testingData.created_date).format("YYYY-MM-DD HH:mm")
                                                : ""}
                                        </span>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} lg={12}>
                                    <Form.Item label="Modified By" name="lastModified_name">
                                        <span>{testingData?.lastModified_name}</span>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} lg={12}>
                                    <Form.Item label="Modified Date" name="modified_date">
                                        <span>
                                            {testingData?.modified_date
                                                ? moment(testingData.modified_date).format("YYYY-MM-DD HH:mm")
                                                : ""}
                                        </span>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                )}
            </Form>
        );
    };

    // Template Selector Modal
    const renderTemplateSelector = () => {
        return (
            <ResponsiveModal
                title="Select a Template for Functional Testing"
                open={showTemplateSelector}
                onCancel={() => setShowTemplateSelector(false)}
                closable={true}
                centered
                width={440}
                wrapClassName="functional-testing-template-modal"
                className="functional-testing-template-modal-inner"
                footer={
                    <div className="functional-testing-template-modal-footer">
                        <Button
                            type="primary"
                            size="large"
                            block
                            className="functional-testing-template-next-btn"
                            onClick={handleTemplateConfirm}
                        >
                            Next
                        </Button>
                    </div>
                }
            >
                <div className="functional-testing-template-modal-body">
                    <label className="functional-testing-template-field-label" htmlFor="functional-testing-detail-template-select">
                        Template
                    </label>
                    <Select
                        id="functional-testing-detail-template-select"
                        className="functional-testing-template-select"
                        style={{ width: "100%" }}
                        placeholder="Choose a template to start from"
                        value={selectedTemplate == null ? undefined : selectedTemplate}
                        onChange={handleTemplateSelect}
                        showSearch
                        optionFilterProp="label"
                        optionLabelProp="children"
                        getPopupContainer={(trigger) => trigger.parentElement || document.body}
                    >
                        {templateOptions.map((template) => (
                            <Option
                                key={template.id === "" ? "blank" : String(template.id)}
                                value={template.id === "" ? "" : String(template.id)}
                                label={formTemplateOptionSearchText(template)}
                                displayLabel={
                                    template.id === ""
                                        ? "Blank Template (Default)"
                                        : getFormTemplateSelectLabel(template)
                                }
                            >
                                <FormTemplateOptionLabel row={template} />
                            </Option>
                        ))}
                    </Select>
                </div>
            </ResponsiveModal>
        );
    };

    if (loading && !testingData && !isCreatePage) {
    return (
            <div style={{ textAlign: "center", padding: "100px 0" }}>
                <Spin size="large" />
        </div>
        );
    }

    return (
        <div className="functional-testing-detail-container">
            <Spin spinning={loading}>
                {isCreatePage && renderTemplateSelector()}

                <DetailTabs
                    defaultActiveKey="1"
                    destroyInactiveTabPane={true}
                    activeKey={activeTabKey}
                    onChange={setActiveTabKey}
                    tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
                    mobileThreshold={1}
                >
                    <TabPane
                        tab={
                            <span>
                                <i className="fa-regular fa-rectangle-list pr-2"></i>Detail
                            </span>
                        }
                        key="1"
                    >
                        <div className="slds-m-around_x-small">
                            <div className="px-4">
                                <div className="w-full">{renderFormFields()}</div>
                            </div>
                        </div>
                    </TabPane>

                    {/* Attachments tab - Hidden in create mode */}
                    {!isCreatePage && (
                        <TabPane
                            tab={
                                <span>
                                    <i className="fa-solid fa-file-pdf pr-2"></i>Attachments
                                </span>
                            }
                            key="2"
                        >
                            {testingData ? (
                                <ResponsiveFileManagement
                                    tableName="functiontesting"
                                    recordId={id}
                                    equipmentId={testingData?.equipment}
                                />
                            ) : (
                                ""
                            )}
                        </TabPane>
                    )}
                </DetailTabs>
            </Spin>
        </div>
    );
};

export default FunctionalTestingDetail;
